// Configuración general del hotel (hotel.config, 1 fila por empresa).
// Hotel → Configuración. Escritura exige nivel de acceso 5 (hotel._exigir_admin
// en config_set); en modo lectura se deshabilita el formulario.
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Settings2 } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { usePermissions } from '@/lib/hooks/usePermissions';
import { Campo, Switch, fieldCls, hotelRpc, QK, HotelConfig } from './shared';

export function ConfigHotel() {
  const qc = useQueryClient();
  const { isAdmin } = usePermissions();
  const puedeEditar = isAdmin();

  const { data, isLoading } = useQuery<HotelConfig | null>({
    queryKey: [QK, 'config'], queryFn: () => hotelRpc<HotelConfig | null>('config_get', {}),
  });
  const [f, setF] = useState<HotelConfig | null>(null);
  const cfg = f ?? data ?? null;
  const set = (patch: Partial<HotelConfig>) => setF({ ...(cfg as HotelConfig), ...patch });

  const activar = useMutation({
    mutationFn: () => hotelRpc<HotelConfig>('config_set', { _d: {} }),
    onSuccess: () => { toast.success('Hotel configurado con valores por defecto'); void qc.invalidateQueries({ queryKey: [QK] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const guardar = useMutation({
    // La fecha de hotel la avanza el cierre nocturno: solo se envía si el usuario
    // la cambió aquí. Reenviar la que se cargó al abrir la pantalla la haría
    // retroceder si un cierre corrió mientras tanto.
    mutationFn: () => {
      const { fecha_hotel, ...resto } = cfg as HotelConfig;
      return hotelRpc<HotelConfig>('config_set', { _d: fecha_hotel !== data?.fecha_hotel ? { ...resto, fecha_hotel } : resto });
    },
    onSuccess: () => { toast.success('Configuración guardada'); setF(null); void qc.invalidateQueries({ queryKey: [QK] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <div className="p-6 space-y-4">
      <h1 className="text-xl font-semibold flex items-center gap-2"><Settings2 className="w-5 h-5" /> Configuración del hotel</h1>

      {isLoading ? (
        <div className="py-10 text-center text-sm text-muted-foreground">Cargando…</div>
      ) : !cfg ? (
        <Card><CardContent className="space-y-3 py-10 text-center">
          <p className="text-sm text-muted-foreground">El hotel aún no está configurado.</p>
          <Button isLoading={activar.isPending} disabled={!isAdmin()} onClick={() => activar.mutate()}>Configurar</Button>
          {!isAdmin() && <p className="text-xs text-amber-700 dark:text-amber-400">Requiere un administrador (nivel de acceso 5).</p>}
        </CardContent></Card>
      ) : (
        <div className="space-y-4">
          {!puedeEditar && (
            <p className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-700 dark:bg-amber-900/20 dark:text-amber-200">
              Solo un administrador (nivel de acceso 5) puede modificar la configuración. Aquí puedes consultarla.
            </p>
          )}
          <div className="flex items-start gap-2 rounded-md border border-sky-300 bg-sky-50 px-3 py-2 text-xs text-sky-900 dark:border-sky-700 dark:bg-sky-900/20 dark:text-sky-200">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>Asigne las cuentas contables de los conceptos <code>hotel_*</code> en Contabilidad → Configuración de cuentas, para que el hospedaje, el servicio, la tasa turística y los depósitos se contabilicen correctamente.</span>
          </div>

          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-base">General</CardTitle></CardHeader>
            <CardContent className="space-y-3 text-sm">
              <Campo label="Nombre comercial">
                <input className={fieldCls} disabled={!puedeEditar} value={cfg.nombre_comercial ?? ''} onChange={(e) => set({ nombre_comercial: e.target.value || null })} />
              </Campo>
              <div className="grid grid-cols-2 gap-3">
                <Campo label="Hora de check-in">
                  <input type="time" className={fieldCls} disabled={!puedeEditar} value={cfg.hora_checkin?.slice(0, 5) ?? ''} onChange={(e) => set({ hora_checkin: e.target.value })} />
                </Campo>
                <Campo label="Hora de check-out">
                  <input type="time" className={fieldCls} disabled={!puedeEditar} value={cfg.hora_checkout?.slice(0, 5) ?? ''} onChange={(e) => set({ hora_checkout: e.target.value })} />
                </Campo>
              </div>
              <Campo label="Fecha del hotel (día operativo)">
                <input type="date" className={`${fieldCls} sm:w-56`} disabled={!puedeEditar} value={cfg.fecha_hotel} onChange={(e) => set({ fecha_hotel: e.target.value })} />
              </Campo>
              <p className="-mt-2 text-xs text-amber-700 dark:text-amber-400">
                Avanza sola con el cierre nocturno. Cambiarla a mano solo para corregir un desfase: afecta llegadas/salidas del día, el housekeeping y el cierre.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-base">Impuestos y cargos</CardTitle></CardHeader>
            <CardContent className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <Campo label="IVA (%)">
                  <input type="number" min={0} max={100} step="0.01" className={fieldCls} disabled={!puedeEditar} value={cfg.iva_pct} onChange={(e) => set({ iva_pct: Number(e.target.value) || 0 })} />
                </Campo>
                <Campo label="Servicio (%)">
                  <input type="number" min={0} max={30} step="0.01" className={fieldCls} disabled={!puedeEditar} value={cfg.servicio_pct} onChange={(e) => set({ servicio_pct: Number(e.target.value) || 0 })} />
                </Campo>
              </div>
              <Switch checked={cfg.iva_feriado_activo} disabled={!puedeEditar} onChange={(v) => set({ iva_feriado_activo: v })} label="Aplicar IVA reducido en feriados" />
              <Switch checked={cfg.factura_fin_mes ?? true} disabled={!puedeEditar} onChange={(v) => set({ factura_fin_mes: v })} label="Facturar automáticamente el último día del mes lo consumido por los huéspedes en casa (IVA en su mes)" />
              <div className="grid grid-cols-2 gap-3 sm:w-96">
                <Campo label="Early check-in (% de la noche)"><input type="number" min={0} max={100} className={fieldCls} disabled={!puedeEditar} value={cfg.early_checkin_pct ?? 50} onChange={(e) => set({ early_checkin_pct: Number(e.target.value) || 0 })} /></Campo>
                <Campo label="Late check-out (% de la noche)"><input type="number" min={0} max={100} className={fieldCls} disabled={!puedeEditar} value={cfg.late_checkout_pct ?? 50} onChange={(e) => set({ late_checkout_pct: Number(e.target.value) || 0 })} /></Campo>
              </div>
              <Switch checked={cfg.cierre_auto ?? false} disabled={!puedeEditar} onChange={(v) => set({ cierre_auto: v })} label="Cierre nocturno automático (lo ejecuta el servidor; si hay salidas vencidas no cierra y lo avisa)" />
              {cfg.cierre_auto && (
                <div className="grid grid-cols-2 gap-3 sm:w-96">
                  <Campo label="Hora del cierre"><input type="time" className={fieldCls} disabled={!puedeEditar} value={(cfg.cierre_auto_hora ?? '03:00').slice(0, 5)} onChange={(e) => set({ cierre_auto_hora: e.target.value })} /></Campo>
                  <Campo label="Zona horaria">
                    <select className={fieldCls} disabled={!puedeEditar} value={cfg.zona_horaria ?? 'America/Guayaquil'} onChange={(e) => set({ zona_horaria: e.target.value })}>
                      <option value="America/Guayaquil">Ecuador continental (GMT-5)</option>
                      <option value="Pacific/Galapagos">Galápagos (GMT-6)</option>
                      <option value="America/Lima">Perú (GMT-5)</option>
                      <option value="America/Bogota">Colombia (GMT-5)</option>
                    </select>
                  </Campo>
                </div>
              )}
              <Switch checked={cfg.exigir_turno_caja ?? false} disabled={!puedeEditar} onChange={(v) => set({ exigir_turno_caja: v })} label="Exigir turno de caja abierto para recibir o devolver efectivo en recepción" />
              <Switch checked={cfg.exigir_tct ?? false} disabled={!puedeEditar} onChange={(v) => set({ exigir_tct: v })} label="Exigir Tarjeta de Control de Tránsito (TCT) a los no residentes en el check-in (Galápagos)" />
              {cfg.iva_feriado_activo && (
                <Campo label="IVA en feriados (%)" className="sm:w-48">
                  <input type="number" min={0} max={100} step="0.01" className={fieldCls} disabled={!puedeEditar} value={cfg.iva_feriado_pct} onChange={(e) => set({ iva_feriado_pct: Number(e.target.value) || 0 })} />
                </Campo>
              )}
              <Campo label="Tasa turística (por persona, por noche)" className="sm:w-56">
                <input type="number" min={0} step="0.01" className={fieldCls} disabled={!puedeEditar} value={cfg.tasa_turistica} onChange={(e) => set({ tasa_turistica: Number(e.target.value) || 0 })} />
              </Campo>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-base">Políticas</CardTitle></CardHeader>
            <CardContent className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <Campo label="Depósito requerido (%)">
                  <input type="number" min={0} max={100} step="0.01" className={fieldCls} disabled={!puedeEditar} value={cfg.deposito_pct} onChange={(e) => set({ deposito_pct: Number(e.target.value) || 0 })} />
                </Campo>
                <Campo label="Horas para pagar el depósito">
                  <input type="number" min={1} className={fieldCls} disabled={!puedeEditar} value={cfg.deposito_horas} onChange={(e) => set({ deposito_horas: Math.max(1, Number(e.target.value) || 1) })} />
                </Campo>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <Campo label="Cancelar sin penalidad hasta (días antes)">
                  <input type="number" min={0} className={fieldCls} disabled={!puedeEditar} value={cfg.cancel_dias} onChange={(e) => set({ cancel_dias: Number(e.target.value) || 0 })} />
                </Campo>
                <Campo label="Penalidad de cancelación (noches)">
                  <input type="number" min={0} className={fieldCls} disabled={!puedeEditar} value={cfg.cancel_penalidad_noches} onChange={(e) => set({ cancel_penalidad_noches: Number(e.target.value) || 0 })} />
                </Campo>
                <Campo label="No-show (noches a cobrar)">
                  <input type="number" min={0} className={fieldCls} disabled={!puedeEditar} value={cfg.noshow_noches} onChange={(e) => set({ noshow_noches: Number(e.target.value) || 0 })} />
                </Campo>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Campo label="Sobreventa permitida (habitaciones)">
                  <input type="number" min={0} className={fieldCls} disabled={!puedeEditar} value={cfg.overbooking_max} onChange={(e) => set({ overbooking_max: Number(e.target.value) || 0 })} />
                </Campo>
                <Campo label="Límite de crédito por folio ($, opcional)">
                  <input type="number" min={0} step="0.01" className={fieldCls} disabled={!puedeEditar} placeholder="Sin límite"
                    value={cfg.limite_credito_default ?? ''} onChange={(e) => set({ limite_credito_default: e.target.value === '' ? null : Number(e.target.value) })} />
                </Campo>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-base">Enlace público</CardTitle></CardHeader>
            <CardContent className="space-y-2 text-sm">
              <Campo label="Slug (motor de reservas directo)" className="sm:w-72">
                <input className={fieldCls} disabled={!puedeEditar} placeholder="mi-hotel" value={cfg.slug_publico ?? ''}
                  onChange={(e) => set({ slug_publico: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') || null })} />
              </Campo>
              <p className="text-xs text-muted-foreground">Define la dirección pública de tu motor de reservas directo. Vacío = deshabilitado.</p>
            </CardContent>
          </Card>

          {puedeEditar && (
            <div className="flex justify-end gap-2">
              {f && <Button variant="outline" onClick={() => setF(null)}>Descartar cambios</Button>}
              <Button isLoading={guardar.isPending} onClick={() => guardar.mutate()}>Guardar</Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
